import { ref, computed, watch } from 'vue'
import store from '@/store'
import { FUNCTION_TYPE } from '@/constants'
import { PLACE_ITEM_TYPE } from '@/js/util/PlaceLabelUtil.js'

/**
 * 「プレイス表示項目」のシステム設定の item_key
 * @type {String}
 */
export const PLACE_DISP_ITEM_KEY = 'place_disp_item'

/**
 * 表示項目と権限の対応
 *
 * プレイス固有の項目（プレイスコード・自動チェックイン範囲・電話番号・FAX番号）と
 * プレイス属性はプレイスの権限で見せる。屋内とセンサー値はそれぞれの権限に従う。
 * 紐付けセンサー名はユーザー側（IFIELD_V4-7247）と同じく機能権限に関係なく表示する
 * @type {Object.<String, String>}
 */
const ITEM_TYPE_AVAILABLE_FUNCTION_TYPE = {
  [PLACE_ITEM_TYPE.PLACE_CODE]: FUNCTION_TYPE.PLACE,
  [PLACE_ITEM_TYPE.CHECKIN_RADIUS]: FUNCTION_TYPE.PLACE,
  [PLACE_ITEM_TYPE.TEL]: FUNCTION_TYPE.PLACE,
  [PLACE_ITEM_TYPE.FAX]: FUNCTION_TYPE.PLACE,
  [PLACE_ITEM_TYPE.INDOOR]: FUNCTION_TYPE.INDOOR,
  [PLACE_ITEM_TYPE.ATTRIBUTE]: FUNCTION_TYPE.PLACE,
  [PLACE_ITEM_TYPE.SENSOR_DATA]: FUNCTION_TYPE.SENSOR,
  [PLACE_ITEM_TYPE.SENSOR_NAMES]: FUNCTION_TYPE.COMMON,
}

/**
 * プレイスリストの表示項目を保持・管理する
 *
 * ユーザーリストの `useUserStatusAdditionalItems` と同じ作りにしてある。
 * 違いは読むシステム設定（`place_disp_item`）と、状態の置き場所（`place` ストア）だけ
 * @returns {Object} 簡易表示フラグ・選択表示項目・選択可能表示項目
 */
export function usePlaceAdditionalItems() {
  /**
   * 簡易表示フラグ
   * @type {Ref.<Boolean>}
   */
  const isSimple = ref(false)
  /**
   * 現在の非表示項目
   * @type {Ref.<Array.<String>>}
   */
  const excludedItems = ref([])

  /**
   * 選択可能表示項目
   *
   * 管理画面で設定した並び順のまま返す。先頭がラベルの2段目に出る項目
   * @type {ComputedRef.<Array.<String>>}
   */
  const selectableItems = computed(() => {
    const itemValue =
      store.getters['systemSetting/getItemValueByItemKey'](PLACE_DISP_ITEM_KEY)
    return (itemValue?.split('|') || []).filter((item) => {
      if (!item) return false
      const itemType = item.split(':')[0]
      return !!store.getters['functionType'](
        ITEM_TYPE_AVAILABLE_FUNCTION_TYPE[itemType]
      )
    })
  })
  /**
   * 選択表示項目
   * @type {ComputedRef.<Array.<String>>}
   */
  const items = computed({
    get: () =>
      selectableItems.value.filter(
        (item) => !excludedItems.value.includes(item)
      ),
    set: (value) => {
      excludedItems.value = selectableItems.value.filter(
        (item) => !value.includes(item)
      )
    },
  })

  // 変更を store に反映
  watch(excludedItems, (value) => {
    store.commit('place/setExcludedItems', value)
  })
  watch(isSimple, (value) => {
    store.commit('place/setIsSimple', value)
  })

  /**
   * 初期値設定
   */
  // store の状態反映
  isSimple.value = store.getters['place/isSimple']
  excludedItems.value = store.getters['place/excludedItems']

  return {
    isSimple,
    items,
    selectableItems,
  }
}
